/**
 * A retention reading becomes one of three words a stranger can read.
 *
 * The panel says *Clearly*, *Less clearly*, *Nearly gone*. Those are a
 * decay reading, and the decay model is not the panel's to invent: it
 * lives at `packages/sdk/src/anima/memory/models.py` and arrives on
 * `server.recall` as `retention`, already computed.
 *
 * ## Where the boundaries come from
 *
 * The engine's curve is `R = e^(-t/S)`, with `t` the elapsed time
 * measured in units of that memory type's own decay constant and `S` the
 * record's strength. So the model has its own natural landmarks, and the
 * tiers are those landmarks rather than three numbers somebody liked:
 *
 * | reading | what has happened | word |
 * |---|---|---|
 * | `R > 1/e` | less than one interval of its own decay has passed | Clearly |
 * | `1/e >= R > 1/e^2` | between one and two intervals | Less clearly |
 * | `R <= 1/e^2` | two intervals or more | Nearly gone |
 *
 * That is why the constants below are written as `Math.exp(-1)` and
 * `Math.exp(-2)` rather than as 0.37 and 0.14. A literal would be a
 * number nobody could check; the expression says which point on the
 * curve it is, and stays correct if the curve is ever re-derived.
 *
 * Because the interval differs per memory type (7, 14 and 30 days for
 * the three kinds that decay), the same word means a different age
 * depending on what kind of thing it is, which is the behaviour the
 * landing page describes: what a character recalls next week is a
 * function of which kind the thing landed in. A kind that does not decay
 * at all, who she is and what she was given to keep, returns exactly 1.0
 * forever and stays in the top tier permanently. The top tier is
 * therefore "does not fade", not "recent", and it is correct that such a
 * record never leaves it.
 *
 * ## What the bottom tier can actually mean
 *
 * Measured rather than assumed, because two behaviours upstream interact
 * and neither is visible from here.
 *
 * The backend drops a record whose retention is under its prune
 * threshold, 0.1, tested with the record's stored strength. Then
 * `reinforce()` adds 0.2 to that strength on access, and the ref is
 * built *after* the bump. So the reading that arrives here is always
 * higher than the one the pruner tested, and the lowest reading that can
 * reach this function is about 0.147, not 0.
 *
 * That is above `1/e^2`. **So a memory being recalled for the first time
 * cannot render as "Nearly gone"** -- it either reads as *Less clearly*
 * or has already been dropped. The bottom tier belongs to something she
 * has returned to before: repeated recall keeps a record alive far past
 * the point a once-seen one is pruned, and it is during that extra life
 * that it genuinely gets close to going.
 *
 * That is a coherent meaning and it is not the obvious one, so it is
 * written down, and
 * `apps/agent-system/tests/animachord/api/stage_ws/test_recall_frame.py`
 * measures the window on every run. If the prune threshold ever rises
 * past `1/e^2` the word becomes unreachable, the panel renders two tiers
 * out of three, and nothing fails -- which is why that check computes the
 * reachable range instead of asserting a constant.
 *
 * ## Why the absent case is not a tier
 *
 * `retention` is optional on the wire. The Python producer always
 * computes it; the Swift client's ref emits the other four fields and
 * not this one. Defaulting a missing reading to `0` would file
 * everything that producer sends under *Nearly gone*, which is the exact
 * shape of the defect this surface was rebuilt for: a display agreeing
 * with a payload for the wrong reason. So an absent reading returns
 * `null` and the panel renders no tier word, because it has not been
 * told one.
 */

import type { MemoryTier } from "@/components/rehearsal/StatePanel";

/** One interval of a memory's own decay has passed. */
export const ONE_INTERVAL = Math.exp(-1);
/** Two have. */
export const TWO_INTERVALS = Math.exp(-2);

/**
 * The word for a retention reading, or `null` when there is no reading.
 *
 * A missing reading is kept distinct from `0`, which is a real reading
 * and the most faded one the model can express.
 */
export function tierFor(
  retention: number | null | undefined,
): MemoryTier | null {
  if (retention === null || retention === undefined) return null;
  if (!Number.isFinite(retention)) return null;
  if (retention > ONE_INTERVAL) return "held";
  if (retention > TWO_INTERVALS) return "settling";
  return "fading";
}
